import { useCallback, useEffect, useRef } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import {
  useCreateSpendingCategoryEstimate,
  useSpendingCategoryEstimates,
  useUpdateSpendingCategoryEstimate,
} from '../../SpendingCategoryEstimate';
import { useSubmitOnChange } from '../../UI';
import { FirmClient, SpendingCategoryEstimate } from '../../generated/graphql';
import { useUpdateClientSpendingService } from '../service';
import { ClientSpendingFormValues, UpdateClientSpendingServiceMutationOptions } from '../types';
import { clientSpendingFormDefaultValues } from '../util';

export interface UseClientSpendingFormArgs {
  firmClient: FirmClient;
  saveOnChange?: boolean;
  onChange?: (data: ClientSpendingFormValues) => void;
  onInvalidSpending?: () => void;
  updateClientSpendingServiceMutationOptions?: UpdateClientSpendingServiceMutationOptions;
}

const latestSpendingCategoryEstimate = (
  estimates: SpendingCategoryEstimate[]
): SpendingCategoryEstimate | undefined => {
  if (estimates.length === 0) return undefined;
  return [...estimates].sort((a, b) => new Date(b.dateTime).getTime() - new Date(a.dateTime).getTime())[0];
};

export const useClientSpendingForm = ({
  onChange,
  onInvalidSpending,
  firmClient,
  saveOnChange,
  updateClientSpendingServiceMutationOptions,
}: UseClientSpendingFormArgs) => {
  const { updateClientSpendingService } = useUpdateClientSpendingService();
  const { createSpendingCategoryEstimate } = useCreateSpendingCategoryEstimate();
  const { updateSpendingCategoryEstimate } = useUpdateSpendingCategoryEstimate();
  const { spendingCategoryEstimates } = useSpendingCategoryEstimates({
    householdID: firmClient?.household?.id,
  });
  const latestEstimate = latestSpendingCategoryEstimate(spendingCategoryEstimates);
  const lastSyncedNotesRef = useRef<string>(latestEstimate?.notes ?? '');

  const methods = useForm<ClientSpendingFormValues>({
    defaultValues: clientSpendingFormDefaultValues(firmClient),
  });

  useEffect(() => {
    const remoteNotes = latestEstimate?.notes ?? '';
    if (remoteNotes === lastSyncedNotesRef.current) return;
    lastSyncedNotesRef.current = remoteNotes;
    methods.setValue('notes', remoteNotes, { shouldDirty: false });
  }, [latestEstimate?.notes, methods]);

  const onSubmit: SubmitHandler<ClientSpendingFormValues> = useCallback(
    (data) => {
      const household = firmClient.household;
      if (!household) {
        onInvalidSpending?.();
        return;
      }
      const householdID = household.id;

      // Round to integer cents and only persist a safe integer — never NaN/Infinity, a
      // float-precision artifact, or a value so large it would overflow the backend `Long`
      // (ELE-726). `maxLength` on the input stops most of this; this is the authoritative guard.
      const spendingInCents = data.spending?.amount != null ? Math.round(data.spending.amount * 100) : null;
      if (spendingInCents != null && Number.isSafeInteger(spendingInCents) && data.spending?.frequency != null) {
        onChange?.(data);
        updateClientSpendingService.mutate(
          {
            householdID,
            spendingInCents,
            spendingDataPointsGroupID: household.spending.id,
            frequency: data.spending.frequency,
          },
          updateClientSpendingServiceMutationOptions
        );
      } else {
        onInvalidSpending?.();
      }

      const notes = data.notes ?? '';
      if (notes === lastSyncedNotesRef.current) return;

      if (latestEstimate) {
        updateSpendingCategoryEstimate.mutate(
          {
            householdID,
            id: latestEstimate.id,
            changeToken: latestEstimate.changeToken,
            changes: { notes },
          },
          {
            onSuccess: () => {
              lastSyncedNotesRef.current = notes;
            },
          }
        );
      } else {
        createSpendingCategoryEstimate.mutate(
          {
            householdID,
            spendingCategoryEstimate: {
              dateTime: new Date().toISOString(),
              categoryEstimates: [],
              notes,
            },
          },
          {
            onSuccess: () => {
              lastSyncedNotesRef.current = notes;
            },
          }
        );
      }
    },
    [
      firmClient,
      latestEstimate,
      onChange,
      onInvalidSpending,
      updateClientSpendingService,
      updateClientSpendingServiceMutationOptions,
      createSpendingCategoryEstimate,
      updateSpendingCategoryEstimate,
    ]
  );

  useSubmitOnChange({
    onChange: onSubmit,
    watch: methods.watch,
    enabled: saveOnChange,
    handleSubmit: methods.handleSubmit,
  });

  return { formMethods: methods, onSubmit };
};
